import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Topbar from "@/components/Topbar";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, BookOpen, Eye, Repeat, HandMetal, CheckCircle2,
  FileSignature, ChevronDown, ChevronRight, ChevronUp, Archive, Sparkles,
} from "lucide-react";

const KIND_ICON = { read: BookOpen, observe: Eye, practice: Repeat, perform: HandMetal, review: CheckCircle2, signoff: FileSignature };
const KIND_LABEL = { read: "Read", observe: "Observe", practice: "Practice", perform: "Perform", review: "Review", signoff: "Sign-off" };
const ROLE_LABEL = {
  experience_guide: "Experience Guide", massage_therapist: "Massage Therapist",
  lead_experience_guide: "Lead EG", lead_massage_therapist: "Lead MT",
  boutique_manager: "GM", recruiter: "Recruiter", leadership: "Leadership",
};

export default function JourneyPathDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [path, setPath] = useState(null);
  const [reference, setReference] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() =>
    Promise.all([
      api.get(`/journey/paths/${id}`).then((r) => setPath(r.data)),
      api.get("/journey/reference").then((r) => setReference(r.data)),
      api.get("/documents", { params: { status: "published" } }).then((r) => {
        const d = r.data;
        setDocs(Array.isArray(d) ? d : (d?.documents || []));
      }).catch(() => setDocs([])),
    ]).finally(() => setLoading(false)), [id]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  if (loading || !path || !reference) return <div className="px-10 py-10 text-stone-400 text-sm">Loading…</div>;

  const editable = reference.can_author;

  return (
    <div data-testid="path-detail">
      <Topbar title={path.title} />
      <div className="px-4 sm:px-10 pb-16 max-w-5xl mx-auto space-y-6">
        <header className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <button onClick={() => nav("/people")} className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1 mb-2">
              <ArrowLeft size={12} /> Back to People
            </button>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] label-eyebrow text-stone-400">{ROLE_LABEL[path.role_target] || path.role_target}</span>
              <StatusPill status={path.status} />
              <span className="text-[10px] label-eyebrow text-stone-400">{path.scope}{path.region ? ` · ${path.region}` : ""}</span>
              <span className="text-[10px] label-eyebrow text-stone-400">v{path.version}</span>
            </div>
            <h1 className="font-serif-display text-3xl text-stone-900 mt-1">{path.title}</h1>
            {path.description && <p className="text-sm text-stone-500 mt-2 max-w-2xl">{path.description}</p>}
          </div>
          {editable && (
            <div className="flex items-center gap-2 shrink-0">
              {path.status === "draft" && (
                <button
                  onClick={async () => {
                    try { await api.post(`/journey/paths/${path.id}/publish`); toast.success("Published."); await load(); }
                    catch (e) { toast.error(e?.response?.data?.detail || "Add at least one chapter first."); }
                  }}
                  data-testid="btn-publish"
                  className="px-4 py-2 rounded-full bg-emerald-600 text-white text-xs hover:bg-emerald-700"
                >
                  Publish
                </button>
              )}
              {path.status === "published" && (
                <button
                  onClick={async () => { await api.post(`/journey/paths/${path.id}/archive`); toast.success("Archived."); await load(); }}
                  data-testid="btn-archive"
                  className="px-3 py-2 rounded-full subtle-border text-xs text-stone-700 hover:bg-stone-50 inline-flex items-center gap-1.5"
                >
                  <Archive size={11} strokeWidth={1.5} /> Archive
                </button>
              )}
            </div>
          )}
        </header>

        <div className="space-y-4">
          {(path.chapters || []).map((c) => (
            <ChapterCard
              key={c.id} path={path} chapter={c} editable={editable} docs={docs}
              onChange={load}
            />
          ))}
          {editable && <AddChapter pathId={path.id} onAdded={load} />}
        </div>

        {(path.chapters || []).length === 0 && !editable && (
          <div className="bg-white subtle-border rounded-2xl p-8 text-center text-sm text-stone-500">
            This Journey doesn't have any chapters yet.
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    published: "bg-emerald-100 text-emerald-700",
    draft: "bg-amber-100 text-amber-700",
    archived: "bg-stone-100 text-stone-400 line-through",
  };
  return <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${styles[status] || styles.draft}`}>{status}</span>;
}

/* ─── Chapter ──────────────────────────────────────────────────── */
function ChapterCard({ path, chapter, editable, docs, onChange }) {
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(false);

  const del = async () => {
    if (!window.confirm(`Delete chapter "${chapter.title}"?`)) return;
    await api.delete(`/journey/paths/${path.id}/chapters/${chapter.id}`);
    onChange();
  };

  return (
    <section className="bg-white subtle-border rounded-2xl overflow-hidden" data-testid={`chapter-card-${chapter.id}`}>
      <header className="flex items-center justify-between gap-3 px-6 py-4">
        <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 min-w-0 text-left">
          {open ? <ChevronDown size={14} strokeWidth={1.25} className="text-stone-400" /> : <ChevronRight size={14} strokeWidth={1.25} className="text-stone-400" />}
          <div className="min-w-0">
            <div className="text-[10px] label-eyebrow text-stone-400">Chapter {chapter.order + 1}</div>
            <div className="font-serif-display text-lg text-stone-900 truncate">{chapter.title}</div>
          </div>
        </button>
        {editable && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => setEditing(true)} data-testid={`chapter-edit-${chapter.id}`} className="text-xs text-stone-500 hover:text-stone-900 px-2 py-1">Edit</button>
            <button onClick={del} data-testid={`chapter-delete-${chapter.id}`} className="text-stone-400 hover:text-rose-600 p-1"><Trash2 size={13} strokeWidth={1.25} /></button>
          </div>
        )}
      </header>
      {chapter.description && <div className="px-6 pb-3 text-xs text-stone-500">{chapter.description}</div>}
      {open && (
        <div className="border-t border-stone-100 divide-y divide-stone-100">
          {(chapter.milestones || []).map((m) => (
            <MilestoneCard key={m.id} path={path} chapter={chapter} milestone={m} editable={editable} docs={docs} onChange={onChange} />
          ))}
          {editable && <AddMilestone pathId={path.id} chapterId={chapter.id} onAdded={onChange} />}
        </div>
      )}
      {editing && <ChapterEditor path={path} chapter={chapter} onClose={() => setEditing(false)} onSaved={onChange} />}
    </section>
  );
}

function AddChapter({ pathId, onAdded }) {
  const [f, setF] = useState({ title: "", description: "" });
  const [open, setOpen] = useState(false);
  const submit = async () => {
    if (!f.title.trim()) return;
    await api.post(`/journey/paths/${pathId}/chapters`, { title: f.title, description: f.description, unlock: "prev_chapter_complete" });
    setF({ title: "", description: "" }); setOpen(false); onAdded();
  };
  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="add-chapter" className="w-full py-3 rounded-2xl border border-dashed border-stone-200 text-sm text-stone-500 hover:bg-stone-50 flex items-center justify-center gap-2">
      <Plus size={13} strokeWidth={1.5} /> Add a chapter
    </button>
  );
  return (
    <div className="bg-white subtle-border rounded-2xl p-4 space-y-2" data-testid="add-chapter-form">
      <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="new-chapter-title" placeholder="Chapter title (e.g. Welcome)" className="w-full px-3 py-2 rounded-lg subtle-border text-sm" />
      <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="new-chapter-description" rows={2} placeholder="A short description" className="w-full px-3 py-2 rounded-lg subtle-border text-sm resize-none" />
      <div className="flex justify-end gap-2">
        <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs text-stone-500">Cancel</button>
        <button onClick={submit} data-testid="new-chapter-submit" className="px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs hover:bg-stone-800">Add chapter</button>
      </div>
    </div>
  );
}

function ChapterEditor({ path, chapter, onClose, onSaved }) {
  const [f, setF] = useState({ title: chapter.title, description: chapter.description || "", unlock: chapter.unlock });
  const save = async () => {
    await api.patch(`/journey/paths/${path.id}/chapters/${chapter.id}`, f);
    onSaved(); onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-3">
        <div className="font-serif-display text-lg text-stone-900">Edit chapter</div>
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full px-3 py-2 rounded-lg subtle-border text-sm" />
        <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} className="w-full px-3 py-2 rounded-lg subtle-border text-sm resize-none" />
        <select value={f.unlock} onChange={(e) => setF({ ...f, unlock: e.target.value })} className="w-full px-3 py-2 rounded-lg subtle-border text-sm">
          <option value="prev_chapter_complete">Unlocks when previous chapter is complete</option>
          <option value="always">Always unlocked</option>
        </select>
        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-stone-500">Cancel</button>
          <button onClick={save} className="px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs">Save</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Milestone ──────────────────────────────────────────────── */
function MilestoneCard({ path, chapter, milestone, editable, docs, onChange }) {
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(false);
  const del = async () => {
    if (!window.confirm(`Delete milestone "${milestone.title}"?`)) return;
    await api.delete(`/journey/paths/${path.id}/chapters/${chapter.id}/milestones/${milestone.id}`);
    onChange();
  };
  return (
    <div className="px-6 py-4" data-testid={`milestone-card-${milestone.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 min-w-0 text-left">
          {open ? <ChevronDown size={12} strokeWidth={1.25} className="text-stone-400" /> : <ChevronRight size={12} strokeWidth={1.25} className="text-stone-400" />}
          <div className="text-sm text-stone-900" style={{ fontWeight: 500 }}>{milestone.title}</div>
        </button>
        {editable && (
          <div className="flex items-center gap-1">
            <button onClick={() => setEditing(true)} data-testid={`milestone-edit-${milestone.id}`} className="text-xs text-stone-500 hover:text-stone-900 px-1.5 py-0.5">Edit</button>
            <button onClick={del} data-testid={`milestone-delete-${milestone.id}`} className="text-stone-400 hover:text-rose-600 p-1"><Trash2 size={11} strokeWidth={1.25} /></button>
          </div>
        )}
      </div>
      {milestone.description && <div className="text-xs text-stone-500 ml-5 mb-2">{milestone.description}</div>}
      {open && (
        <div className="ml-5 space-y-2">
          {(milestone.activities || []).map((a) => (
            <ActivityRow key={a.id} path={path} chapter={chapter} milestone={milestone} activity={a} editable={editable} docs={docs} onChange={onChange} />
          ))}
          {editable && <AddActivity pathId={path.id} chapterId={chapter.id} milestoneId={milestone.id} docs={docs} onAdded={onChange} />}
        </div>
      )}
      {editing && <MilestoneEditor path={path} chapter={chapter} milestone={milestone} onClose={() => setEditing(false)} onSaved={onChange} />}
    </div>
  );
}

function AddMilestone({ pathId, chapterId, onAdded }) {
  const [f, setF] = useState({ title: "", description: "" });
  const [open, setOpen] = useState(false);
  const submit = async () => {
    if (!f.title.trim()) return;
    await api.post(`/journey/paths/${pathId}/chapters/${chapterId}/milestones`, { ...f, unlock: "prev_milestone_complete" });
    setF({ title: "", description: "" }); setOpen(false); onAdded();
  };
  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid={`add-milestone-${chapterId}`} className="w-full mx-6 my-3 py-2 rounded-xl border border-dashed border-stone-200 text-xs text-stone-500 hover:bg-stone-50 flex items-center justify-center gap-2" style={{ width: "auto" }}>
      <Plus size={11} strokeWidth={1.5} /> Add a milestone
    </button>
  );
  return (
    <div className="px-6 py-3 space-y-2" data-testid={`add-milestone-form-${chapterId}`}>
      <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="new-milestone-title" placeholder="Milestone title" className="w-full px-3 py-2 rounded-lg subtle-border text-sm" />
      <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} data-testid="new-milestone-description" placeholder="What does mastery look like?" className="w-full px-3 py-2 rounded-lg subtle-border text-sm resize-none" />
      <div className="flex justify-end gap-2">
        <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs text-stone-500">Cancel</button>
        <button onClick={submit} data-testid="new-milestone-submit" className="px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs">Add</button>
      </div>
    </div>
  );
}

function MilestoneEditor({ path, chapter, milestone, onClose, onSaved }) {
  const [f, setF] = useState({ title: milestone.title, description: milestone.description || "", unlock: milestone.unlock });
  const save = async () => {
    await api.patch(`/journey/paths/${path.id}/chapters/${chapter.id}/milestones/${milestone.id}`, f);
    onSaved(); onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-3">
        <div className="font-serif-display text-lg text-stone-900">Edit milestone</div>
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full px-3 py-2 rounded-lg subtle-border text-sm" />
        <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} className="w-full px-3 py-2 rounded-lg subtle-border text-sm resize-none" />
        <select value={f.unlock} onChange={(e) => setF({ ...f, unlock: e.target.value })} className="w-full px-3 py-2 rounded-lg subtle-border text-sm">
          <option value="prev_milestone_complete">Unlocks when previous milestone is complete</option>
          <option value="always">Always unlocked</option>
        </select>
        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-stone-500">Cancel</button>
          <button onClick={save} className="px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs">Save</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Activity ──────────────────────────────────────────────── */
function ActivityRow({ path, chapter, milestone, activity, editable, docs, onChange }) {
  const Icon = KIND_ICON[activity.kind] || BookOpen;
  const [editing, setEditing] = useState(false);
  const del = async () => {
    if (!window.confirm(`Delete activity "${activity.title}"?`)) return;
    await api.delete(`/journey/paths/${path.id}/chapters/${chapter.id}/milestones/${milestone.id}/activities/${activity.id}`);
    onChange();
  };
  const doc = useMemo(() => docs.find((d) => d.id === activity.document_id), [docs, activity.document_id]);
  return (
    <div className="flex items-start justify-between gap-2 rounded-xl p-3 subtle-border bg-white" data-testid={`activity-card-${activity.id}`}>
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <span className="shrink-0 w-7 h-7 rounded-lg subtle-border flex items-center justify-center text-stone-600">
          <Icon size={12} strokeWidth={1.5} />
        </span>
        <div className="min-w-0">
          <div className="text-[10px] label-eyebrow text-stone-400 flex items-center gap-2 flex-wrap">
            <span>{KIND_LABEL[activity.kind] || activity.kind}</span>
            {activity.estimated_minutes ? <span>· {activity.estimated_minutes} min</span> : null}
            {activity.signoff_role && activity.signoff_role !== "self" && <span className="text-amber-600">· Sign-off by {activity.signoff_role.toUpperCase()}</span>}
          </div>
          <div className="text-sm text-stone-900 truncate">{activity.title}</div>
          {activity.description && <div className="text-xs text-stone-500 mt-1">{activity.description}</div>}
          {doc && <div className="text-[10px] text-stone-500 mt-1">Linked doc: <Link to={`/documents/${doc.id}`} className="text-stone-700 hover:underline">{doc.title}</Link></div>}
        </div>
      </div>
      {editable && (
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => setEditing(true)} data-testid={`activity-edit-${activity.id}`} className="text-xs text-stone-500 hover:text-stone-900 px-1.5 py-0.5">Edit</button>
          <button onClick={del} data-testid={`activity-delete-${activity.id}`} className="text-stone-400 hover:text-rose-600 p-1"><Trash2 size={11} strokeWidth={1.25} /></button>
        </div>
      )}
      {editing && <ActivityEditor path={path} chapter={chapter} milestone={milestone} activity={activity} docs={docs} onClose={() => setEditing(false)} onSaved={onChange} />}
    </div>
  );
}

function AddActivity({ pathId, chapterId, milestoneId, docs, onAdded }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ kind: "read", title: "", description: "", document_id: "", signoff_role: "self", estimated_minutes: "" });
  const submit = async () => {
    if (!f.title.trim()) return;
    const body = {
      kind: f.kind, title: f.title, description: f.description,
      document_id: f.document_id || null,
      signoff_role: f.signoff_role || null,
      estimated_minutes: f.estimated_minutes ? Number(f.estimated_minutes) : null,
    };
    await api.post(`/journey/paths/${pathId}/chapters/${chapterId}/milestones/${milestoneId}/activities`, body);
    setF({ kind: "read", title: "", description: "", document_id: "", signoff_role: "self", estimated_minutes: "" });
    setOpen(false); onAdded();
  };
  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid={`add-activity-${milestoneId}`} className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1">
      <Plus size={11} strokeWidth={1.5} /> Add an activity
    </button>
  );
  return (
    <div className="rounded-xl border border-dashed border-stone-200 p-3 space-y-2" data-testid={`add-activity-form-${milestoneId}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="new-activity-kind" className="px-3 py-1.5 rounded-lg subtle-border text-sm">
          {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={f.signoff_role} onChange={(e) => setF({ ...f, signoff_role: e.target.value })} data-testid="new-activity-signoff" className="px-3 py-1.5 rounded-lg subtle-border text-sm">
          <option value="self">Self-signed</option>
          <option value="mentor">Mentor signs</option>
          <option value="gm">GM signs</option>
          <option value="leadership">Leadership signs</option>
        </select>
        <input type="number" min="0" value={f.estimated_minutes} onChange={(e) => setF({ ...f, estimated_minutes: e.target.value })} data-testid="new-activity-minutes" placeholder="min" className="w-20 px-3 py-1.5 rounded-lg subtle-border text-sm" />
      </div>
      <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="new-activity-title" placeholder="Activity title" className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm" />
      <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} placeholder="What's involved?" className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm resize-none" />
      <select value={f.document_id} onChange={(e) => setF({ ...f, document_id: e.target.value })} data-testid="new-activity-doc" className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm">
        <option value="">No linked document</option>
        {docs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
      </select>
      <div className="flex justify-end gap-2">
        <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs text-stone-500">Cancel</button>
        <button onClick={submit} data-testid="new-activity-submit" className="px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs">Add</button>
      </div>
    </div>
  );
}

function ActivityEditor({ path, chapter, milestone, activity, docs, onClose, onSaved }) {
  const [f, setF] = useState({
    kind: activity.kind, title: activity.title, description: activity.description || "",
    document_id: activity.document_id || "", signoff_role: activity.signoff_role || "self",
    estimated_minutes: activity.estimated_minutes || "",
  });
  const save = async () => {
    const body = {
      kind: f.kind, title: f.title, description: f.description,
      document_id: f.document_id || null,
      signoff_role: f.signoff_role || null,
      estimated_minutes: f.estimated_minutes ? Number(f.estimated_minutes) : null,
    };
    await api.patch(`/journey/paths/${path.id}/chapters/${chapter.id}/milestones/${milestone.id}/activities/${activity.id}`, body);
    onSaved(); onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-3">
        <div className="font-serif-display text-lg text-stone-900">Edit activity</div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="px-3 py-1.5 rounded-lg subtle-border text-sm">
            {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={f.signoff_role} onChange={(e) => setF({ ...f, signoff_role: e.target.value })} className="px-3 py-1.5 rounded-lg subtle-border text-sm">
            <option value="self">Self-signed</option>
            <option value="mentor">Mentor</option>
            <option value="gm">GM</option>
            <option value="leadership">Leadership</option>
          </select>
          <input type="number" min="0" value={f.estimated_minutes} onChange={(e) => setF({ ...f, estimated_minutes: e.target.value })} placeholder="min" className="w-20 px-3 py-1.5 rounded-lg subtle-border text-sm" />
        </div>
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm" />
        <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm resize-none" />
        <select value={f.document_id} onChange={(e) => setF({ ...f, document_id: e.target.value })} className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm">
          <option value="">No linked document</option>
          {docs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
        </select>
        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-stone-500">Cancel</button>
          <button onClick={save} className="px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs">Save</button>
        </div>
      </div>
    </div>
  );
}
